import { GenericStore } from 'src/util/mobx/generic-store/generic.store'
import { createStore } from 'src/util/mobx/generic-store/store-creator'
import { GenericStoreAsyncMethod } from 'src/util/mobx/generic-store/decorator/async-method.decorator'
import {
  OrganisationOverview,
  PerformanceReviewTemplate,
  CreateTemplateDto,
  UpdateTemplateDto,
  SubmitResponsesDto,
  PerformanceReviewResponse,
  AnswerComment,
  UserInfo,
  PerformanceReviewInsightsDto,
} from '../model/my-team-performance'
import * as myTeamPerformanceApi from '../api/my-team-performance-api'
import { CourseDevelopmentChart, CourseHighlights } from '../model/my-team-elearning'
import * as myTeamElearningApi from '../api/my-team-elearning-api'
import {
  SkillGapInsightsResponse,
  SkillGapPerformance,
  UnusedSkillGapMetrics,
  unusedSkillGapsOverview,
  UsedSkillGapMetrics,
  UsedSkillGapsOverview,
  UsedSkillsOverview,
  RatingChart,
} from '../model/my-team-skill-gaps'
import * as myTeamSkillGapsApi from '../api/my-team-skill-gaps-api'
import { TeamOrganisationOverviewResponse, TeamOrder, TeamSkillGapMetrics, TeamSortBy } from '../model/my-team'
import * as myTeamApi from '../api/my-team-api'
import {
  RoleOrganisationOverview,
  RoleOrganisationOverviewOrder,
  RoleOrganisationOverviewSortBy,
  TeamRolesSkillGapMetricsOverview,
  TeamRolesUsersSkillGapMetricsList,
} from '../model/my-team-roles'
import * as myTeamRolesApi from '../api/my-team-roles-api'
import { TeamDashboardMetrics, TeamDashboardSkillGapMetrics, TeamDashboardTeamMetrics } from '../model/my-team-dashboard'
import * as myTeamDashboardApi from '../api/my-team-dashboard-api'
import { UserNotification } from '../api/my-team-dashboard-api'
import { ComparisonEntity, ComparisonResponse } from '../model/my-team-comparison'
import * as myTeamComparisonApi from '../api/my-team-comparison-api'

type ComparisonDateRange = {
  id: string
  startDate: string
  endDate: string
}

type ComparisonRequestBody = {
  entity1: ComparisonDateRange
  entity2?: ComparisonDateRange
  minRating?: number
  maxRating?: number
}

const createInitialTeamOrganisationOverview = (): TeamOrganisationOverviewResponse => ({
  teams: [],
  totalTeams: 0,
  filteredTeams: 0,
  totalUsers: 0,
  highestAvgRatedTeam: null,
  highestGapTeam: null,
})

const createInitialTeamRolesOrganisationOverview = (): RoleOrganisationOverview => ({
  roles: [],
  totalRoles: 0,
  filteredRoles: 0,
  totalSkills: 0,
  highestAvgRatedRole: null,
  highestGapRole: null,
})

const createInitialTeamRolesUsersSkillGapMetricsList = (): TeamRolesUsersSkillGapMetricsList => ({
  users: [],
  totalUsers: 0,
  filteredUsers: 0,
})

const createInitialUsedSkillsOverview = (): UsedSkillsOverview => ({
  count: 0,
  skillSets: [],
})

export class myTeamStore extends GenericStore {
  organisationOverview: OrganisationOverview[] = []
  teamOrganisationOverview: TeamOrganisationOverviewResponse = createInitialTeamOrganisationOverview()
  teamRolesOrganisationOverview: RoleOrganisationOverview = createInitialTeamRolesOrganisationOverview()
  courseDevelopment?: CourseDevelopmentChart
  courseHighlights: CourseHighlights[] = []
  allUsedSkills: UsedSkillsOverview = createInitialUsedSkillsOverview()
  usedSkillGapsOverview: UsedSkillGapsOverview = {}
  allUnusedSkills: UsedSkillsOverview = createInitialUsedSkillsOverview()
  unusedSkillGapsOverview: unusedSkillGapsOverview = {}
  skillGapPerformance?: SkillGapPerformance
  skillGapInsights?: SkillGapInsightsResponse
  teamSkillGapMetrics: TeamSkillGapMetrics[] = []
  teamSkillGapMetricLevelIds: string[] = []
  teamRolesSkillGapMetricsOverview: TeamRolesSkillGapMetricsOverview = {}
  teamRolesUsersSkillGapMetricsList: TeamRolesUsersSkillGapMetricsList = createInitialTeamRolesUsersSkillGapMetricsList()
  teamDashboardMetrics?: TeamDashboardMetrics
  teamDashboardSkillGapMetrics?: TeamDashboardSkillGapMetrics
  teamDashboardSkillsByRating: RatingChart[] = []
  teamDashboardTeamMetrics?: TeamDashboardTeamMetrics
  userNotifications: UserNotification[] = []

  // Performance Review
  performanceReviewTemplates: PerformanceReviewTemplate[] = []
  currentPerformanceReviewTemplate: PerformanceReviewTemplate | null = null
  currentMeetingParticipants: UserInfo[] = []
  currentUserResponses: PerformanceReviewResponse[] = []
  currentUserComment = ''
  currentAnswerComments: AnswerComment[] = []
  currentPerformanceReviewInsights: PerformanceReviewInsightsDto | null = null

  // Comparison
  comparisonTeams: ComparisonEntity[] = []
  comparisonRoles: ComparisonEntity[] = []
  comparisonUsers: ComparisonEntity[] = []
  teamComparison?: ComparisonResponse
  roleComparison?: ComparisonResponse
  userComparison?: ComparisonResponse

  constructor() {
    super('myTeamStore')

    super.observe(this)
  }

  private resetPerformanceReviewState() {
    this.performanceReviewTemplates = []
    this.currentPerformanceReviewTemplate = null
    this.currentMeetingParticipants = []
    this.currentUserResponses = []
    this.currentUserComment = ''
    this.currentAnswerComments = []
  }

  private resetComparisonState() {
    this.comparisonTeams = []
    this.comparisonRoles = []
    this.comparisonUsers = []
    this.teamComparison = undefined
    this.roleComparison = undefined
    this.userComparison = undefined
  }

  reset() {
    this.organisationOverview = []
    this.teamOrganisationOverview = createInitialTeamOrganisationOverview()
    this.teamRolesOrganisationOverview = createInitialTeamRolesOrganisationOverview()
    this.courseDevelopment = undefined
    this.courseHighlights = []
    this.allUsedSkills = createInitialUsedSkillsOverview()
    this.usedSkillGapsOverview = {}
    this.allUnusedSkills = createInitialUsedSkillsOverview()
    this.unusedSkillGapsOverview = {}
    this.skillGapPerformance = undefined
    this.skillGapInsights = undefined
    this.teamSkillGapMetrics = []
    this.teamSkillGapMetricLevelIds = []
    this.teamRolesSkillGapMetricsOverview = {}
    this.teamRolesUsersSkillGapMetricsList = createInitialTeamRolesUsersSkillGapMetricsList()
    this.teamDashboardMetrics = undefined
    this.teamDashboardSkillGapMetrics = undefined
    this.teamDashboardSkillsByRating = []
    this.teamDashboardTeamMetrics = undefined
    this.userNotifications = []
    this.resetPerformanceReviewState()
    this.resetComparisonState()
  }

  @GenericStoreAsyncMethod()
  async getOrganisationOverview() {
    this.organisationOverview = await myTeamPerformanceApi.getOrganisationOverview()
  }

  @GenericStoreAsyncMethod()
  async getTeamOrganisationOverview(
    skip: number,
    take: number,
    search?: string,
    sortBy?: TeamSortBy,
    order?: TeamOrder,
  ) {
    this.teamOrganisationOverview = await myTeamApi.getTeamOrganisationOverview(skip, take, search ?? '', sortBy, order)
  }

  @GenericStoreAsyncMethod()
  async getTeamRolesOrganisationOverview(
    levelId: string,
    skip: number,
    take: number,
    search?: string,
    sortBy?: RoleOrganisationOverviewSortBy,
    order?: RoleOrganisationOverviewOrder,
  ) {
    this.teamRolesOrganisationOverview = await myTeamRolesApi.getTeamRolesOrganisationOverview(levelId, skip, take, search ?? '', sortBy, order)
  }

  @GenericStoreAsyncMethod()
  async getCourseDevelopment(startDate: string, endDate: string, orgLevelIds: string[]) {
    this.courseDevelopment = await myTeamElearningApi.getCourseDevelopment(startDate, endDate, orgLevelIds)
  }

  @GenericStoreAsyncMethod()
  async getCourseHighlights(startDate: string, endDate: string, orgLevelIds: string[]) {
    this.courseHighlights = await myTeamElearningApi.getCourseHighlights(startDate, endDate, orgLevelIds)
  }

  @GenericStoreAsyncMethod()
  async getAllUsedSkills(skip: number, take: number, searchText: string, sortBy: 'asc' | 'multi') {
    this.allUsedSkills = await myTeamSkillGapsApi.getAllUsedSkills(skip, take, searchText, sortBy)
  }

  @GenericStoreAsyncMethod()
  async getUsedSkillGaps(setName: string, setIds: string[]) {
    const data: UsedSkillGapMetrics = await myTeamSkillGapsApi.getUsedSkillGaps(setIds)
    this.usedSkillGapsOverview[setName] = data
  }

  @GenericStoreAsyncMethod()
  async getAllUnusedSkills(skip: number, take: number, searchText: string) {
    this.allUnusedSkills = await myTeamSkillGapsApi.getAllUnusedSkills(skip, take, searchText)
  }

  @GenericStoreAsyncMethod()
  async getUnusedSkillGaps(setName: string, setIds: string[]) {
    const data: UnusedSkillGapMetrics = await myTeamSkillGapsApi.getUnusedSkillGaps(setIds)
    this.unusedSkillGapsOverview[setName] = data
  }

  @GenericStoreAsyncMethod()
  async getSkillGapPerformance(criticalGapThreshold: number) {
    this.skillGapPerformance = await myTeamSkillGapsApi.getSkillGapPerformance(criticalGapThreshold)
  }

  @GenericStoreAsyncMethod()
  async getSkillGapInsights(skillIds: string[], minRating: number | null, maxRating: number | null) {
    this.skillGapInsights = await myTeamSkillGapsApi.getSkillGapInsights(skillIds, minRating, maxRating)
  }

  @GenericStoreAsyncMethod()
  async fetchTeamSkillGapMetricsForLevel(levelId: string) {
    await this.fetchTeamSkillGapMetricsBatch([levelId])
    this.teamSkillGapMetricLevelIds = [levelId]
  }

  @GenericStoreAsyncMethod()
  async fetchTeamSkillGapMetricsBatch(levelIds: string[]) {
    const uniqueLevelIds = [...new Set(levelIds.filter(Boolean))]

    if (!uniqueLevelIds.length) {
      this.teamSkillGapMetrics = []
      this.teamSkillGapMetricLevelIds = []

      return
    }

    const data = await myTeamApi.getTeamSkillGapMetricsBatch(uniqueLevelIds)
    this.teamSkillGapMetrics = uniqueLevelIds.map(levelId => data[levelId])
    this.teamSkillGapMetricLevelIds = uniqueLevelIds
  }

  @GenericStoreAsyncMethod()
  async fetchTeamRolesSkillGapMetricsForLevel(levelId: string) {
    await this.fetchTeamRolesSkillGapMetricsBatch([levelId])
  }

  @GenericStoreAsyncMethod()
  async fetchTeamRolesSkillGapMetricsBatch(levelIds: string[]) {
    const uniqueLevelIds = [...new Set(levelIds.filter(Boolean))]

    if (!uniqueLevelIds.length) {
      return
    }

    uniqueLevelIds.forEach((levelId) => {
      delete this.teamRolesSkillGapMetricsOverview[levelId]
    })

    const data = await myTeamRolesApi.getTeamRolesSkillGapMetricsBatch(uniqueLevelIds)

    uniqueLevelIds.forEach((levelId) => {
      const metrics = data[levelId]

      if (metrics) {
        this.teamRolesSkillGapMetricsOverview[levelId] = metrics
      }
    })
  }

  @GenericStoreAsyncMethod()
  async getTeamRolesUsersSkillGapMetrics(
    levelId: string,
    skip: number,
    take: number,
    search?: string,
    sortBy?: RoleOrganisationOverviewSortBy,
    order?: RoleOrganisationOverviewOrder,
  ) {
    this.teamRolesUsersSkillGapMetricsList = await myTeamRolesApi.getTeamRolesUsersSkillGapMetrics(levelId, skip, take, search, sortBy, order)
  }

  @GenericStoreAsyncMethod()
  async getTeamDashboardMetrics() {
    this.teamDashboardMetrics = await myTeamDashboardApi.getTeamDashboardMetrics()
  }

  @GenericStoreAsyncMethod()
  async getTeamDashboardSkillGapMetrics() {
    this.teamDashboardSkillGapMetrics = await myTeamDashboardApi.getTeamDashboardSkillGapMetrics()
  }

  @GenericStoreAsyncMethod()
  async getTeamDashboardTeamMetrics() {
    this.teamDashboardTeamMetrics = await myTeamDashboardApi.getTeamDashboardTeamMetrics()
  }

  @GenericStoreAsyncMethod()
  async getUserNotifications() {
    this.userNotifications = await myTeamDashboardApi.getUserNotifications()
  }

  // Performance Review Methods
  @GenericStoreAsyncMethod()
  async getMeetingById(id: string) {
    this.currentPerformanceReviewTemplate = await myTeamPerformanceApi.getMeetingById(id)
  }

  // Get participants using target_id
  @GenericStoreAsyncMethod()
  async getMeetingParticipants(id: string) {
    this.currentMeetingParticipants = await myTeamPerformanceApi.getTemplateParticipants(id)
  }

  @GenericStoreAsyncMethod()
  async getUserResponses(templateId: string, userId: string) {
    this.currentUserResponses = await myTeamPerformanceApi.getUserResponses(templateId, userId)
  }

  @GenericStoreAsyncMethod()
  async getComment(templateId: string, userId: string) {
    const result = await myTeamPerformanceApi.getPerformanceReviewComment(templateId, userId)
    this.currentUserComment = result?.content ?? ''
  }

  async saveComment(templateId: string, userId: string, content: string) {
    await myTeamPerformanceApi.savePerformanceReviewComment(templateId, userId, content)
  }

  @GenericStoreAsyncMethod()
  async getAnswerComments(templateId: string, userId: string) {
    this.currentAnswerComments = await myTeamPerformanceApi.getAnswerComments(templateId, userId)
  }

  @GenericStoreAsyncMethod()
  async getMyAnswerComments(templateId: string) {
    this.currentAnswerComments = await myTeamPerformanceApi.getMyAnswerComments(templateId)
  }

  async saveAnswerComment(templateId: string, userId: string, questionId: string, content: string) {
    await myTeamPerformanceApi.saveAnswerComment(templateId, userId, questionId, content)
  }

  @GenericStoreAsyncMethod()
  async createPerformanceReviewTemplate(dto: CreateTemplateDto) {
    await myTeamPerformanceApi.createPerformanceReviewTemplate(dto)
  }

  @GenericStoreAsyncMethod()
  async getAllPerformanceReviewTemplates() {
    this.performanceReviewTemplates = await myTeamPerformanceApi.getAllPerformanceReviewTemplates()
  }

  @GenericStoreAsyncMethod()
  async getPerformanceReviewTemplatesByUser(userId?: string) {
    this.performanceReviewTemplates = await myTeamPerformanceApi.getPerformanceReviewTemplatesByUser(userId)
  }

  @GenericStoreAsyncMethod()
  async updatePerformanceReviewTemplate(id: string, dto: UpdateTemplateDto) {
    const updatedTemplate = await myTeamPerformanceApi.updatePerformanceReviewTemplate(id, dto)
    const index = this.performanceReviewTemplates.findIndex(t => t.id === id)

    if (index !== -1) {
      this.performanceReviewTemplates[index] = updatedTemplate
    }

    if (this.currentPerformanceReviewTemplate?.id === id) {
      this.currentPerformanceReviewTemplate = updatedTemplate
    }
  }

  @GenericStoreAsyncMethod()
  async deletePerformanceReviewTemplate(id: string) {
    await myTeamPerformanceApi.deletePerformanceReviewTemplate(id)
    this.performanceReviewTemplates = this.performanceReviewTemplates.filter(t => t.id !== id)

    if (this.currentPerformanceReviewTemplate?.id === id) {
      this.currentPerformanceReviewTemplate = null
    }
  }

  @GenericStoreAsyncMethod()
  async createPerformanceReviewResponses(dto: SubmitResponsesDto): Promise<PerformanceReviewResponse[]> {
    return myTeamPerformanceApi.createPerformanceReviewResponses(dto)
  }

  @GenericStoreAsyncMethod()
  async downloadPerformanceReviewPdf(id: string, name?: string) {
    const data = await myTeamPerformanceApi.downloadPerformanceReviewPdf(id)
    const blob = new Blob([data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const date = new Date().toISOString()
      .split('T')[0]
    const slug = name ? name.replace(/[^a-zA-Z0-9-_ åäöÅÄÖ]/g, '').replace(/\s+/g, '-') : ''
    a.download = slug ? `${slug}-${date}.pdf` : `performance-review-${date}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  @GenericStoreAsyncMethod()
  async getPerformanceReviewInsights(id: string) {
    this.currentPerformanceReviewInsights = await myTeamPerformanceApi.getPerformanceReviewInsights(id)
  }

  @GenericStoreAsyncMethod()
  async getTeamDashboardSkillsByRating(rating: number) {
    this.teamDashboardSkillsByRating = await myTeamDashboardApi.getTeamDashboardSkillsByRating(rating)
  }

  // Comparison Methods
  @GenericStoreAsyncMethod()
  async getComparisonTeams() {
    this.comparisonTeams = await myTeamComparisonApi.getComparisonTeams()
  }

  @GenericStoreAsyncMethod()
  async getComparisonRoles() {
    this.comparisonRoles = await myTeamComparisonApi.getComparisonRoles()
  }

  @GenericStoreAsyncMethod()
  async getComparisonUsers() {
    this.comparisonUsers = await myTeamComparisonApi.getComparisonUsers()
  }

  private buildComparisonRequestBody(
    entity1Id: string,
    entity1FromDate: string,
    entity1ToDate: string,
    entity2Id?: string,
    entity2FromDate?: string,
    entity2ToDate?: string,
    minRating?: number,
    maxRating?: number,
  ): ComparisonRequestBody {
    const requestBody: ComparisonRequestBody = {
      entity1: {
        id: entity1Id,
        startDate: entity1FromDate,
        endDate: entity1ToDate,
      },
    }

    if (entity2Id && entity2FromDate && entity2ToDate) {
      requestBody.entity2 = {
        id: entity2Id,
        startDate: entity2FromDate,
        endDate: entity2ToDate,
      }
    }

    if (minRating !== undefined) {
      requestBody.minRating = minRating
    }
    if (maxRating !== undefined) {
      requestBody.maxRating = maxRating
    }

    return requestBody
  }

  @GenericStoreAsyncMethod()
  async compareTeams(entity1Id: string, entity1FromDate: string, entity1ToDate: string, entity2Id?: string, entity2FromDate?: string, entity2ToDate?: string, minRating?: number, maxRating?: number) {
    const requestBody = this.buildComparisonRequestBody(entity1Id, entity1FromDate, entity1ToDate, entity2Id, entity2FromDate, entity2ToDate, minRating, maxRating)
    this.teamComparison = await myTeamComparisonApi.compareTeams(requestBody)
  }

  @GenericStoreAsyncMethod()
  async compareRoles(entity1Id: string, entity1FromDate: string, entity1ToDate: string, entity2Id?: string, entity2FromDate?: string, entity2ToDate?: string, minRating?: number, maxRating?: number) {
    const requestBody = this.buildComparisonRequestBody(entity1Id, entity1FromDate, entity1ToDate, entity2Id, entity2FromDate, entity2ToDate, minRating, maxRating)
    this.roleComparison = await myTeamComparisonApi.compareRoles(requestBody)
  }

  @GenericStoreAsyncMethod()
  async compareUsers(entity1Id: string, entity1FromDate: string, entity1ToDate: string, entity2Id?: string, entity2FromDate?: string, entity2ToDate?: string, minRating?: number, maxRating?: number) {
    const requestBody = this.buildComparisonRequestBody(entity1Id, entity1FromDate, entity1ToDate, entity2Id, entity2FromDate, entity2ToDate, minRating, maxRating)
    this.userComparison = await myTeamComparisonApi.compareUsers(requestBody)
  }
}

export const {
  storeInstance: myTeamStoreInstance,
  useStore: useMyTeamStore,
} = createStore(new myTeamStore())
